<script>
   /**************************************************************/
   /* AcceptancePlotSeries                                       */
   /* --------------------                                       */
   /* shows plot with score and orthogonal distances             */
   /**************************************************************/

   import { Vector } from 'mdatools/arrays';
   import { getContext } from 'svelte';
   import { LABELS_COLOR, CAT_COLORS, MAX_LABEL_SYMBOLS, getClassColors } from '../../../shared/plots/Defaults.js';

   export let resobj;                     // JSON with SIMCA results
   export let transType = 'no';           // transformation type
   export let showLabels = 'hide';        // show labels or not?
   export let xMax = 0;                   // to return maximum x-coordinate for series
   export let yMax = 0;                   // to return maximum y-coordinate for series
   export let selected;                   // selected sample
   export let scope = 'objects';          // plot scope ('objects' or 'variables')
   export let ncomp = 1;                  // number of components selected by user
   export let isAlt = false;              // acceptance plot for members or alternative?
   export let colorBy = 'roles';          // how to color group data points ('roles' or 'classes')

   /*****************************************/
   /* Component code                        */
   /*****************************************/

   // get axes context and adjust x margins
   const axes = getContext('axes');
   const tX = axes.tX;
   const tY = axes.tY;
   const isOk = axes.isOk;

   // define colors for the points
   $: catColors = colorBy == 'classes' ? getClassColors(resobj.classNames) : CAT_COLORS;

   // convert array with colors to JSON with categories as keys
   $: pch = isAlt ? '■' : '●';

   // reactive calculations for distance values
   let xd, yd, c, l, t, max_xd, max_yd;
   $: if (resobj && ncomp > 0) {

      // define a shortcut to make subsets easier
      const res = resobj.v[ncomp - 1];

      // select distances to show and their scales
      let res_xd, res_yd, res_xd0, res_yd0, cat;
      if (scope === 'objects') {
         res_xd = res.h;
         res_yd = res.q;
         res_xd0 = res.h0;
         res_yd0 = res.q0;
         c = Array(resobj.nrows);
         l = resobj.objlabels.map(v => v.slice(0, MAX_LABEL_SYMBOLS));
         cat = isAlt ? (colorBy == 'roles' ? res.a : resobj.classes) : res.c;
      } else {
         res_xd = res.g;
         res_yd = res.r;
         res_xd0 = res.g0;
         res_yd0 = res.r0;
         c = Array(resobj.ncols);
         l = resobj.varlabels.map(v => v.slice(0, MAX_LABEL_SYMBOLS));
         cat = Array(resobj.ncols).fill('regular')
      }

      // prepare vectors for transformed distances
      const n = res_xd.length;
      xd = Vector.zeros(n);
      yd = Vector.zeros(n);
      max_xd = 0;
      max_yd = 0;

      if (transType === 'log') {
         for (let i = 0; i < n; i++) {
            xd.v[i] = Math.log(1 + res_xd[i]/res_xd0);
            yd.v[i] = Math.log(1 + res_yd[i]/res_yd0);
            max_xd = Math.max(max_xd, xd.v[i]);
            max_yd = Math.max(max_yd, yd.v[i]);
            c[i] = catColors[cat[i]];
         }
      } else {
         for (let i = 0; i < n; i++) {
            xd.v[i] = res_xd[i]/res_xd0;
            yd.v[i] = res_yd[i]/res_yd0;
            max_xd = Math.max(max_xd, xd.v[i]);
            max_yd = Math.max(max_yd, yd.v[i]);
            c[i] = catColors[cat[i]];
         }
      }

      xMax = max_xd;
      yMax = max_yd;
   } else {
      xd = null;
      yd = null;
      c = null;
      l = null;
   }
   // reactive calculations for coordinates
   let x, y
   $: if ($isOk && xd && yd) {
      x = axes.transform(xd, $tX.coords);
      y = axes.transform(yd, $tY.coords);
   } else {
      x = null;
      y = null;
   }

   // styles for points, bars, labels, etc.
   const ptStyle = `font-size:1em;fill:none;text-anchor:middle;cursor:default;stroke-width:2px;user-select:none;`;
   const lbStyle = `font-size:0.85em;fill:${LABELS_COLOR};text-anchor:middle;cursor:default;stroke:${LABELS_COLOR};stroke-width:0.1px;user-select:none;`;
   const slStyle = `font-size:1.2em;fill:none;text-anchor:middle;cursor:default;stroke-width:3px;user-select:none;`;

</script>

{#if $isOk && x && y}

   <g class="series series-points" title="distance-points" style={ptStyle} dominant-baseline="middle" text-anchor="middle">
   {#each x as v, i}
      <text x={x[i]} y={y[i]} stroke={c[i]}>{pch}</text>
   {/each}
   </g>

   {#if showLabels === 'show' && l && l.length > 0}
   <g class="series series-labels" title="distance-labels" style={lbStyle} dominant-baseline="middle" text-anchor="middle">
      {#each x as v, i}
         <text x={x[i]} y={y[i]} dy={-12}>{l[i]}</text>
      {/each}
   </g>
   {/if}

   {#if selected >= 0}
      <rect x={0} y={0} width={'100%'} height={'100%'} fill="#ffffffa0" />
      <g class="series series-points" title="distance-points" style={slStyle} dominant-baseline="middle" text-anchor="middle">
      <text x={x[selected]} y={y[selected]} stroke={c[selected]} >{pch}</text>
      </g>

      {#if showLabels === 'show' && l && l.length > 0}
      <g class="series series-labels" title="distance-labels" style={lbStyle} dominant-baseline="middle" text-anchor="middle">
         <text x={x[selected]} y={y[selected]} dy={-12}>{l[selected]}</text>
      </g>
      {/if}

   {/if}
{/if}

